import { z } from 'zod';

export type TimestampValue = number | string | null | undefined;

export type NumericTimestampUnit = 'milliseconds' | 'seconds-or-milliseconds';

/** A date selection in the browser's local calendar. */
export interface LocalDateRange {
  from: Date | undefined;
  to?: Date;
}

/** Inclusive/exclusive UTC timestamp bounds. */
export interface UtcTimestampRange {
  from?: string;
  to?: string;
}

export interface DateTimeFormatOptions {
  locale?: string;
  dateStyle?: Intl.DateTimeFormatOptions['dateStyle'];
  timeStyle?: Intl.DateTimeFormatOptions['timeStyle'];
  timeZone?: string;
  numericUnit?: NumericTimestampUnit;
}

/** Whether a string is a real UTC ISO-8601 timestamp with a `Z` offset. */
export function isUtcIsoTimestamp(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?Z$/.exec(value);
  if (!match) return false;
  const parsed = new Date(value);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.getUTCFullYear() === Number(match[1]) &&
    parsed.getUTCMonth() + 1 === Number(match[2]) &&
    parsed.getUTCDate() === Number(match[3]) &&
    parsed.getUTCHours() === Number(match[4]) &&
    parsed.getUTCMinutes() === Number(match[5]) &&
    parsed.getUTCSeconds() === Number(match[6])
  );
}

/** Strict UTC ISO-8601 timestamp validation for URL and form boundaries. */
export const utcIsoTimestampSchema = z.string().refine(isUtcIsoTimestamp, {
  message: 'invalid_utc_iso_timestamp',
});

/** True only for a complete, ascending UTC timestamp interval. */
export function hasAscendingUtcTimestampRange(
  from: string | undefined,
  to: string | undefined,
): boolean {
  return from !== undefined && to !== undefined && Date.parse(from) < Date.parse(to);
}

/**
 * Convert an inclusive local calendar selection to UTC inclusive/exclusive bounds.
 *
 * The end bound starts on the local calendar day after `to` (or `from` for a
 * single-day selection), so it remains correct across daylight-saving changes.
 */
export function localDateRangeToUtcTimestampRange(
  range: LocalDateRange | undefined,
): UtcTimestampRange {
  if (range?.from === undefined) return {};
  const from = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate());
  const finalDay = range.to ?? range.from;
  const to = new Date(finalDay.getFullYear(), finalDay.getMonth(), finalDay.getDate());
  to.setDate(to.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Restore an inclusive local calendar selection from UTC inclusive/exclusive bounds.
 *
 * Missing or invalid end bounds preserve a valid start as a single-day selection.
 */
export function utcTimestampRangeToLocalDateRange(
  fromValue: string | undefined,
  toValue: string | undefined,
): LocalDateRange | undefined {
  if (fromValue === undefined) return undefined;
  const from = new Date(fromValue);
  if (Number.isNaN(from.getTime())) return undefined;
  if (toValue === undefined) return { from };
  const to = new Date(toValue);
  if (Number.isNaN(to.getTime())) return { from };
  to.setDate(to.getDate() - 1);
  return { from, to };
}

/** Resolve a wire timestamp to epoch milliseconds, returning `null` when it is absent or invalid. */
export function toEpochMilliseconds(
  value: TimestampValue,
  numericUnit: NumericTimestampUnit = 'milliseconds',
): number | null {
  if (value == null) return null;
  const milliseconds =
    typeof value === 'number'
      ? numericUnit === 'seconds-or-milliseconds' && value < 1e12
        ? value * 1_000
        : value
      : Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

/** Format a valid timestamp with explicit, reusable calendar-display semantics. */
export function formatDateTime(
  value: TimestampValue,
  {
    locale,
    dateStyle = 'medium',
    timeStyle = 'short',
    timeZone,
    numericUnit = 'milliseconds',
  }: DateTimeFormatOptions = {},
): string | null {
  const milliseconds = toEpochMilliseconds(value, numericUnit);
  if (milliseconds === null) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle, timeStyle, timeZone }).format(milliseconds);
}

/** Format a timestamp or return the caller's display fallback. */
export function formatDateTimeOrFallback(
  value: TimestampValue,
  fallback = '—',
  options: DateTimeFormatOptions = {},
): string {
  return formatDateTime(value, options) ?? fallback;
}

/** Format the calendar portion of a valid timestamp. */
export function formatDate(
  value: TimestampValue,
  {
    locale,
    dateStyle = 'medium',
    timeZone,
    numericUnit = 'milliseconds',
  }: Omit<DateTimeFormatOptions, 'timeStyle'> = {},
): string | null {
  const milliseconds = toEpochMilliseconds(value, numericUnit);
  if (milliseconds === null) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle, timeZone }).format(milliseconds);
}

/** An ISO timestamp as a local `datetime-local` value (`2026-09-05T12:24`). */
export function toDatetimeLocalValue(iso: string): string {
  const milliseconds = Date.parse(iso);
  if (Number.isNaN(milliseconds)) return '';
  const date = new Date(milliseconds);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A `datetime-local` value as an ISO timestamp, or `null` when unparseable. */
export function fromDatetimeLocalValue(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const date = new Date(year, month, day, hours, minutes);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day ||
    date.getHours() !== hours ||
    date.getMinutes() !== minutes
  ) {
    return null;
  }
  const milliseconds = date.getTime();
  return Number.isFinite(milliseconds) ? new Date(milliseconds).toISOString() : null;
}

/** Parse a local `datetime-local` value into the equivalent local `Date`. */
export function datetimeLocalValueToDate(value: string): Date | null {
  const iso = fromDatetimeLocalValue(value);
  return iso === null ? null : new Date(iso);
}

/** Replace the calendar date in a local `datetime-local` value, preserving its minute. */
export function withDatetimeLocalDate(value: string, date: Date): string | null {
  const current = datetimeLocalValueToDate(value);
  if (current === null) return null;
  current.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
  return toDatetimeLocalValue(current.toISOString());
}

/** Replace the clock time in a local `datetime-local` value. */
export function withDatetimeLocalTime(
  value: string,
  hours: number,
  minutes: number,
): string | null {
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  const current = datetimeLocalValueToDate(value);
  if (current === null) return null;
  current.setHours(hours, minutes, 0, 0);
  return toDatetimeLocalValue(current.toISOString());
}

/** Local calendar day key (`2026-09-06`) for grouping a day's work. */
export function dayKey(epochMs: number): string {
  const date = new Date(epochMs);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The browser's resolved IANA time zone, with UTC as a stable fallback. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Calendar day key for a timestamp in an explicit IANA time zone. */
export function dayKeyInTimeZone(value: TimestampValue, timeZone = browserTimeZone()): string {
  const epoch = toEpochMilliseconds(value);
  if (epoch === null) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(epoch);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type === 'year' || part.type === 'month' || part.type === 'day')
      .map((part) => [part.type, part.value]),
  );
  return `${values.year}-${values.month}-${values.day}`;
}

/** Today's calendar day key in an explicit IANA time zone. */
export function todayKeyInTimeZone(timeZone = browserTimeZone()): string {
  return dayKeyInTimeZone(Date.now(), timeZone);
}

/**
 * A day key back to a local `Date` for formatting.
 *
 * `new Date('2026-09-06')` parses a date-only string as **UTC** midnight, which
 * renders as the previous day in any negative-offset zone. Constructing from parts
 * keeps the key and the heading on the same calendar day.
 */
export function dayKeyToLocalDate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}
